import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import {
  ALERT_DEVICE_TARGET_ALL,
  fetchPoolAlertDevices,
  forceStopPoolAlertDevices,
  startPoolAlertDevices,
  type PoolAlertDeviceListItem,
  type PoolAlertDeviceType,
} from '../../services/poolAlertDeviceService';
import { formatSirenApiErrorMessage } from '../../utils/sirenApiErrorMessage';

type Props = {
  poolId: number | null;
  deviceType: PoolAlertDeviceType;
  title: string;
  enabled: boolean;
  /** 선택 대상이 바뀔 때 (알림 전송 시 같은 선택으로 start) */
  onTargetChange?: (target: string) => void;
  /** 등록 장비 수 (0이면 알림 전송 시 울림 체크를 무시) */
  onDeviceCountChange?: (count: number) => void;
};

export const EventAlertDeviceControls: React.FC<Props> = ({
  poolId,
  deviceType,
  title,
  enabled,
  onTargetChange,
  onDeviceCountChange,
}) => {
  const [devices, setDevices] = useState<PoolAlertDeviceListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const runningRef = useRef(false);
  const lastTargetRef = useRef('');
  const onTargetChangeRef = useRef(onTargetChange);
  const onDeviceCountChangeRef = useRef(onDeviceCountChange);
  const selectAllRef = useRef(selectAll);
  const selectedIdsRef = useRef(selectedIds);
  /** poolId:deviceType 가 바뀔 때만 선택 초기화 */
  const selectionScopeRef = useRef<string>('');

  useEffect(() => {
    onTargetChangeRef.current = onTargetChange;
  }, [onTargetChange]);

  useEffect(() => {
    onDeviceCountChangeRef.current = onDeviceCountChange;
  }, [onDeviceCountChange]);

  useEffect(() => {
    selectAllRef.current = selectAll;
  }, [selectAll]);

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  useEffect(() => {
    runningRef.current = running;
  }, [running]);

  const buildTarget = useCallback(
    (all: boolean, ids: number[], total: number): string => {
      if (total <= 0) return '';
      if (all || ids.length >= total) return ALERT_DEVICE_TARGET_ALL;
      if (ids.length === 0) return '';
      return [...ids].sort((a, b) => a - b).join(',');
    },
    []
  );

  const emitTarget = useCallback(
    (all: boolean, ids: number[], total: number) => {
      const target = buildTarget(all, ids, total);
      lastTargetRef.current = target;
      onTargetChangeRef.current?.(target);
    },
    [buildTarget]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!poolId || !enabled) {
        setDevices([]);
        if (!enabled) {
          // 다이얼로그만 닫힌 경우 선택·ref 유지 (위험알림 미리보기 중 재로드로 선택 유실 방지)
          return;
        }
        onTargetChangeRef.current?.('');
        onDeviceCountChangeRef.current?.(0);
        return;
      }

      const scope = `${poolId}:${deviceType}`;
      const scopeChanged = selectionScopeRef.current !== scope;
      if (scopeChanged) {
        selectionScopeRef.current = scope;
      }

      setLoading(true);
      setFeedback(null);
      try {
        const list = await fetchPoolAlertDevices(poolId, deviceType);
        if (cancelled) return;
        setDevices(list);
        onDeviceCountChangeRef.current?.(list.length);
        const idSet = new Set(list.map((d) => d.id));

        if (list.length === 0) {
          setSelectedIds([]);
          setSelectAll(false);
          emitTarget(false, [], 0);
          return;
        }

        if (scopeChanged) {
          // 시설/유형이 바뀐 첫 로드 — 등록된 경광등/부저 전부 기본 선택
          // (이벤트 발생·전송 시에도 체크된 전 채널로 신호가 나가게)
          const ids = list.map((d) => d.id);
          setSelectedIds(ids);
          setSelectAll(true);
          emitTarget(true, ids, list.length);
        } else {
          // 같은 시설 — 기존 선택 유지 (위험알림 버튼으로 부모 리렌더 시 체크 해제 방지)
          const prevAll = selectAllRef.current;
          const prevIds = selectedIdsRef.current.filter((id) => idSet.has(id));
          if (prevAll && list.length > 0) {
            setSelectAll(true);
            setSelectedIds(list.map((d) => d.id));
            emitTarget(true, list.map((d) => d.id), list.length);
          } else {
            setSelectAll(false);
            setSelectedIds(prevIds);
            emitTarget(false, prevIds, list.length);
          }
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setDevices([]);
        onDeviceCountChangeRef.current?.(0);
        setFeedback(formatSirenApiErrorMessage(e, '장비 목록을 불러오기 실패'));
        if (scopeChanged) {
          onTargetChangeRef.current?.('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [poolId, deviceType, enabled, emitTarget]);

  const handleStart = async () => {
    if (!poolId) return;
    if (devices.length === 0) {
      setFeedback('등록된 장비 없음. 시설 관리 → 경광등/부저에서 등록.');
      return;
    }
    const target = buildTarget(selectAll, selectedIds, devices.length);
    if (!target) {
      setFeedback('장비를 하나 이상 선택');
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const res = await startPoolAlertDevices({
        poolId,
        deviceType,
        target,
        alertLevel: 'danger',
      });
      lastTargetRef.current = target;
      onTargetChangeRef.current?.(target);
      if (res.success) {
        setRunning(true);
        runningRef.current = true;
        setFeedback(`${title} 동작 중`);
      } else {
        setFeedback(
          res.results?.find((r) => r.error)?.error ||
            '장비 연결에 실패. 설정·네트워크를 확인'
        );
      }
    } catch (e: unknown) {
      setFeedback(formatSirenApiErrorMessage(e, `${title} 시작에 실패`));
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    if (!poolId) return;
    setBusy(true);
    try {
      const res = await forceStopPoolAlertDevices(poolId, deviceType);
      if (res.success) {
        setFeedback(`${title} 중지 완료.`);
      } else {
        setFeedback(
          res.results?.find((r) => r.error)?.error ||
            `${title} 중지 명령 전송. 현장 릴레이 접속 확인`
        );
      }
    } catch (e: unknown) {
      setFeedback(formatSirenApiErrorMessage(e, `${title} 중지에 실패`));
    } finally {
      runningRef.current = false;
      setRunning(false);
      setBusy(false);
    }
  };

  const handleForceStop = async () => {
    if (!poolId) return;
    setBusy(true);
    try {
      const res = await forceStopPoolAlertDevices(poolId, deviceType);
      setFeedback(
        res.success
          ? `모든 ${title} 중지 완료.`
          : res.results?.find((r) => r.error)?.error ||
              `강제 중지 실패 — data_gateway·릴레이를 확인.`
      );
    } catch (e: unknown) {
      setFeedback(formatSirenApiErrorMessage(e, `${title} 강제 중지에 실패`));
    } finally {
      runningRef.current = false;
      setRunning(false);
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setRunning(false);
      runningRef.current = false;
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title} (해당 시설)
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            목록 불러오는 중…
          </Typography>
        </Box>
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          등록된 {title} 없음. 시설 관리 → 경광등/부저 탭에서 등록
        </Typography>
      ) : (
        <FormGroup sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                disabled={busy || running}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSelectAll(checked);
                  const ids = checked ? devices.map((d) => d.id) : [];
                  setSelectedIds(ids);
                  emitTarget(checked, ids, devices.length);
                }}
              />
            }
            label={`모든 ${title}`}
          />
          {devices.map((s) => {
            const checked = selectAll || selectedIds.includes(s.id);
            return (
              <FormControlLabel
                key={s.id}
                sx={{ ml: 1 }}
                control={
                  <Checkbox
                    checked={checked}
                    disabled={busy || running || selectAll}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setSelectAll(false);
                      setSelectedIds((prev) => {
                        const next = isChecked
                          ? [...new Set([...prev, s.id])].sort((a, b) => a - b)
                          : prev.filter((i) => i !== s.id);
                        emitTarget(false, next, devices.length);
                        return next;
                      });
                    }}
                  />
                }
                label={s.label}
              />
            );
          })}
        </FormGroup>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => void handleStart()}
          disabled={busy || loading || devices.length === 0 || running}
        >
          {title} 울림
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void handleStop()}
          disabled={busy || devices.length === 0}
        >
          {title} 중단
        </Button>
        <Button
          variant="text"
          color="error"
          onClick={() => void handleForceStop()}
          disabled={busy || devices.length === 0}
        >
          {title} 강제중지
        </Button>
      </Box>
      {feedback ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {feedback}
        </Typography>
      ) : null}
    </Box>
  );
};

export async function stopAllEventAlertDevices(poolId: number): Promise<void> {
  await Promise.all([
    forceStopPoolAlertDevices(poolId, 'siren', { silentError: true }).catch(() => undefined),
    forceStopPoolAlertDevices(poolId, 'buzzer', { silentError: true }).catch(() => undefined),
  ]);
}
